import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchInvoiceEditData } from '../../../../features/customer/customer.slice';
import { getTimesheetWorkingHours } from '../../../../features/timesheet/timesheet.slice';
import AddTimesheetSidebar from './AddTimesheetSidebar';
import AddTimeoffSidebar from './AddTimeoffSidebar';
import AddCustomerModal from '../../../../components/add-customer-modal/components/AddCustomerModal';
import UnsavedTimesheetWarningModal from './UnsavedTimesheetWarningModal';

interface TimesheetDataEntry {
  id: number;
  workingDate: string;
  dateId: string;
  number: number;
  unit: string;
  vat: number;
  isArticleShownInInvoice: boolean;
  amountPerUnit: number;
  amount: number;
  articleNumber: string;
  workingDescription: string;
  houseworkType: string;
  workingHour: number;
  CustomWorkingDescription: string;
  userId: number;
}

interface TimesheetHeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedClient: string;
  onClientChange: (client: string) => void;
  selectedDate: string;
  onDateChange: (date: string) => void;
  onInvoiceByTimesheet: () => void;
  onAddRecord: (timesheetData?: TimesheetDataEntry[]) => void;
  fromDate?: string;
  toDate?: string;
  onResetClient?: () => void;
  hasNewEntries?: boolean;
  timesheetData?: TimesheetDataEntry[];
  onSaveTimesheet?: () => Promise<void>;
}

interface TimesheetFormData {
  clientDetails: string;
  taskDetails: string;
  fromDate: string;
  toDate: string;
  hours: string;
  pricingType: string;
  price: string;
}

interface TimeoffFormData {
  timeOffType: string;
  clientDetails: string;
  fromDate: string;
  toDate: string;
  timeOffPricing: string;
  currency: string;
  pricing: string;
  price: string;
  hoursForDay: string;
}

const TimesheetHeader: React.FC<TimesheetHeaderProps> = ({
  searchQuery,
  onSearchChange,
  selectedClient,
  onClientChange,
  selectedDate,
  onDateChange,
  onInvoiceByTimesheet,
  onAddRecord,
  fromDate = '',
  toDate = '',
  onResetClient,
  hasNewEntries = false,
  timesheetData = [],
  onSaveTimesheet,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Get user ID from Redux auth state
  const userId = useSelector((state: any) => state.auth?.user?.userId);
  
  // Get invoice edit data from Redux customer state
  const { invoiceEditData, loading, error, customers } = useSelector((state: any) => state.customer);
  
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isTimeoffSidebarOpen, setIsTimeoffSidebarOpen] = useState(false);
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const [isClientDropdownDisabled, setIsClientDropdownDisabled] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingAction, setPendingAction] = useState<'SELECT_CLIENT' | 'ADD_CLIENT' | 'INVOICE_BY_TIMESHEET' | 'ADD_RECORD' | null>(null);
  const clientDropdownRef = useRef<HTMLDivElement>(null);
  const isAutoSelectingClient = useRef(false);

  // Fetch user data when component mounts
  useEffect(() => {
    if (userId) {
      dispatch(fetchInvoiceEditData(userId) as any);
    }
  }, [dispatch, userId]);

  // Re-enable dropdown when client is reset
  useEffect(() => {
    if (!selectedClient || selectedClient === '') {
      setIsClientDropdownDisabled(false);
    }
  }, [selectedClient]);

  // Fetch timesheet data when client is selected
  useEffect(() => {
    // Skip API call if client is being auto-selected after adding a record
    if (isAutoSelectingClient.current) {
      isAutoSelectingClient.current = false;
      return;
    }

    if (selectedClient && selectedClient !== '' && selectedClient !== 'all') {
      const customerId = parseInt(selectedClient);
      if (!isNaN(customerId)) {
        dispatch(
          getTimesheetWorkingHours({
            fromDate: fromDate || '',
            toDate: toDate || '',
            customerId: String(customerId),
            pageNumber: 1
          }) as any
        );
      }
    }
  }, [dispatch, selectedClient, fromDate, toDate]);

  const handleAddTimesheetRecord = () => {
    if (hasNewEntries) {
      setPendingAction('ADD_RECORD');
      setShowWarningModal(true);
      return;
    }
    setShowAddMenu(false);
    setIsSidebarOpen(true);
  };

  const handleTimeOffAbsence = () => {
    setShowAddMenu(false);
    setIsTimeoffSidebarOpen(true);
  };

  const handleClientDropdownClick = () => {
    if (isClientDropdownDisabled) return;
    
    if (hasNewEntries) {
      setPendingAction('SELECT_CLIENT');
      setShowWarningModal(true);
      return;
    }
    
    setIsClientDropdownOpen(!isClientDropdownOpen);
  };

  const handleAddClientClick = () => {
    if (hasNewEntries) {
      setPendingAction('ADD_CLIENT');
      setShowWarningModal(true);
      return;
    }
    
    setShowCustomerModal(true);
  };

  const handleInvoiceByTimesheetClick = () => {
    if (hasNewEntries) {
      setPendingAction('INVOICE_BY_TIMESHEET');
      setShowWarningModal(true);
      return;
    }
    
    onInvoiceByTimesheet();
  };

  const handleWarningModalClose = () => {
    // Cancel button - just close modal without any action
    setShowWarningModal(false);
    setPendingAction(null);
  };

  const handleSaveAndProceed = async () => {
    if (!onSaveTimesheet) return;
    
    setIsSaving(true);
    try {
      await onSaveTimesheet();
      
      // After successful save, proceed with the pending action
      if (pendingAction) {
        proceedWithAction(pendingAction);
      }
      
      // Close modal
      setShowWarningModal(false);
      setPendingAction(null);
    } catch (error) {
      console.error('Error saving timesheet:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const proceedWithAction = (action: 'SELECT_CLIENT' | 'ADD_CLIENT' | 'INVOICE_BY_TIMESHEET' | 'ADD_RECORD') => {
    switch (action) {
      case 'SELECT_CLIENT':
        setIsClientDropdownOpen(true);
        break;
      case 'ADD_CLIENT':
        setShowCustomerModal(true);
        break;
      case 'INVOICE_BY_TIMESHEET':
        onInvoiceByTimesheet();
        break;
      case 'ADD_RECORD':
        setShowAddMenu(false);
        setIsSidebarOpen(true);
        break;
    }
  };

  const handleSidebarSubmit = (data: TimesheetFormData, timesheetDataArray: any[]) => {
    onAddRecord(timesheetDataArray);
    
    // Set the selected client from the saved timesheet record
    if (timesheetDataArray && timesheetDataArray.length > 0) {
      const customerId = timesheetDataArray[0]?.customerId;
      if (customerId) {
        // Mark as auto-selecting to prevent API call
        isAutoSelectingClient.current = true;
        // Disable dropdown after auto-selecting client
        setIsClientDropdownDisabled(true);
        onClientChange(String(customerId));
      }
    }
  };

  const handleTimeoffSidebarSubmit = (data: TimeoffFormData) => {
    onAddRecord();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowAddMenu(false);
      }
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target as Node)) {
        setIsClientDropdownOpen(false);
      }
    };

    if (showAddMenu || isClientDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAddMenu, isClientDropdownOpen]);

  return (
    <>
      <div className="timesheet-records-header">
      <h1 className="timesheet-records-title">
        {t('TIMESHEET_RECORDS')}
      </h1>

      <div className="timesheet-records-actions">
        {/* Search Input */}
        

        {/* Client Dropdown */}
        <div className="timesheet-dropdown custom-dropdown" ref={clientDropdownRef}>
          <div 
            className={`dropdown-trigger ${isClientDropdownOpen ? 'open' : ''} ${isClientDropdownDisabled ? 'disabled' : ''}`}
            onClick={handleClientDropdownClick}
            style={isClientDropdownDisabled ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
          >
            <span className="dropdown-value" style={{color: 'var(--text-icons-light-base-second, #68727D)',
fontFamily: "Plus Jakarta Sans",
fontSize: "15px",
fontStyle: "normal",
fontWeight: "400",
lineHeight: "22px" /* 146.667% */}}>
              {selectedClient && customers && customers.length > 0
                ? customers.find((c: any) => String(c.customerId || c.id) === String(selectedClient))?.name || t('SELECT_CLIENT')
                : t('SELECT_CLIENT')}
            </span>
            <svg className={`dropdown-arrow ${isClientDropdownOpen ? 'rotated' : ''}`} width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path 
                d="M5 7.5L10 12.5L15 7.5" 
                stroke="#68727D" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {isClientDropdownOpen && !isClientDropdownDisabled && (
            <div className="dropdown-list">
              <div className="dropdown-content">
                {customers && customers.length > 0 ? (
                  customers.map((customer: any) => (
                    <div
                      key={customer.customerId || customer.id}
                      className={`list-item ${String(selectedClient) === String(customer.customerId || customer.id) ? 'selected' : ''}`}
                      onClick={() => {
                        onClientChange(String(customer.customerId || customer.id));
                        setIsClientDropdownOpen(false);
                      }}
                    >
                      <div className="text-input">
                        <div className="paragraph-container">
                          <p className="option-text">{customer.name}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="list-item no-results">
                    <div className="text-input">
                      <div className="paragraph-container">
                        <p className="option-text">{t('NO_CLIENTS_FOUND')}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Add Client Button */}
        <button 
          type="button" 
          className="btn-invoice-timesheet"
          onClick={handleAddClientClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            borderRadius: '50px',
            border: 'none',
            backgroundColor: '#6750b8',
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: 700,
            fontFamily: 'Plus Jakarta Sans',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#544196'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6750b8'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
            <path d="M6.33333 1V11.6667M11.6667 6.33333L1 6.33333" stroke="#ffffff" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span>{t('ADD_CLIENT')}</span>
        </button>

        {/* Date Picker */}
       

        {/* Invoice By Timesheet Button */}
        <button 
          type="button" 
          className="btn-invoice-timesheet"
          onClick={handleInvoiceByTimesheetClick}
        >
         <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
  <path d="M6.33333 1V11.6667M11.6667 6.33333L1 6.33333" stroke="#252525" stroke-width="2" stroke-linecap="round"/>
</svg>
         <span style={{color: "var(--text-icons-light-base-main, #252525)",
fontFamily: "Plus Jakarta Sans",
fontSize: "14px",
fontStyle: "normal",
fontWeight: 700,
lineHeight: "20px", /* 142.857% */
letterSpacing: "0.1px"}}>{t('INVOICE_BY_TIMESHEET')}</span>
        </button>

        {/* Add Record Button */}
        <div className="add-record-wrapper custom-dropdown" ref={dropdownRef}>
          <div 
            className={`dropdown-trigger btn-add-record ${showAddMenu ? 'open' : ''}`}
            onClick={() => setShowAddMenu(!showAddMenu)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M7.99996 2.66666V13.3333M13.3333 7.99999L2.66663 7.99999" stroke="#544196" stroke-width="2" stroke-linecap="round"/>
</svg>
            <span className="dropdown-value" style={{color: "var(--Brand-700, #544196)",
fontFamily: "Plus Jakarta Sans",
fontSize: '14px',
fontStyle: 'normal',
fontWeight: '700',
lineHeight: '20px', 
letterSpacing: '0.1px'}}>{t('ADD_RECORD')}</span>
            <svg className={`dropdown-arrow dropdown-icon-brand ${showAddMenu ? 'rotated' : ''}`} width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path 
                d="M4 6L8 10L12 6" 
                stroke="#544196" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {showAddMenu && (
            <div className="dropdown-list add-record-dropdown">
              <div className="dropdown-content">
                <div
                  className="list-item dropdown-item"
                  onClick={handleAddTimesheetRecord}
                >
                  <div className="text-input">
                    <div className="paragraph-container">
                      <p className="option-text">{t('TIMESHEET_RECORD')}</p>
                    </div>
                  </div>
                </div>
                {/* <div
                  className="list-item dropdown-item"
                  onClick={handleTimeOffAbsence}
                >
                  <div className="text-input">
                    <div className="paragraph-container">
                      <p className="option-text">{t('TIME_OFF_ABSENSE')}</p>
                    </div>
                  </div>
                </div> */}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    <AddTimesheetSidebar
      isOpen={isSidebarOpen}
      onClose={() => setIsSidebarOpen(false)}
      onSubmit={handleSidebarSubmit}
    />
    <AddTimeoffSidebar
      isOpen={isTimeoffSidebarOpen}
      onClose={() => setIsTimeoffSidebarOpen(false)}
      onSubmit={handleTimeoffSidebarSubmit}
    />
    <AddCustomerModal
      show={showCustomerModal}
      onHide={() => setShowCustomerModal(false)}
      onSave={() => {
        setShowCustomerModal(false);
        // Refetch customers after adding new customer
        if (userId) {
          dispatch(fetchInvoiceEditData(userId) as any);
        }
      }}
    />
    <UnsavedTimesheetWarningModal
      isOpen={showWarningModal}
      onClose={handleWarningModalClose}
      onConfirm={handleSaveAndProceed}
      isSaving={isSaving}
    />
    </>
  );
};

export default TimesheetHeader;
